"use client"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Clock, Loader2 } from "lucide-react"
import { apiClient } from "../lib/apiClient"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip"

interface Defaulter {
  managerId: number
  userId: number
  manager: string
  user: string
  duration: string
  startDate: string
  hasDraft: boolean
  hasSubmitted: boolean
  hasDefaulted: boolean
}

interface MissingTimesheetModalProps {
  isOpen: boolean
  onClose: () => void
}

export function MissingTimesheetModal({ isOpen, onClose }: MissingTimesheetModalProps) {
  const [defaulters, setDefaulters] = useState<Defaulter[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Custom scrollbar styles
  const scrollbarStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: rgba(239, 68, 68, 0.1);
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(239, 68, 68, 0.4);
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: rgba(239, 68, 68, 0.6);
    }
  `

  useEffect(() => {
    if (isOpen) {
      fetchDefaulters()
    } else {
      // Reset state when modal closes
      setDefaulters([])
      setError(null)
    }
  }, [isOpen])

  const fetchDefaulters = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient.get("/Dashboard/getmanagerdefaulters")
      if (response.data && Array.isArray(response.data)) {
        setDefaulters(response.data)
      } else {
        setDefaulters([])
      }
    } catch (err: any) {
      console.error("Error fetching defaulters:", err)
      setError("Failed to load missing timesheets. Please try again.")
      setDefaulters([])
    } finally {
      setLoading(false)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  // Group defaulters by user
  const groupedDefaulters = defaulters.reduce((acc, defaulter) => {
    const userId = defaulter.userId
    if (!acc[userId]) {
      acc[userId] = {
        userId: defaulter.userId,
        userName: defaulter.user,
        timesheets: []
      }
    }
    acc[userId].timesheets.push({
      duration: defaulter.duration,
      startDate: defaulter.startDate,
      hasDraft: defaulter.hasDraft,
      hasSubmitted: defaulter.hasSubmitted,
      hasDefaulted: defaulter.hasDefaulted
    })
    return acc
  }, {} as Record<number, { userId: number; userName: string; timesheets: Array<{ duration: string; startDate: string; hasDraft: boolean; hasSubmitted: boolean; hasDefaulted: boolean }> }>)

  const uniqueUsers = Object.values(groupedDefaulters)

  return (
    <>
      <style>{scrollbarStyles}</style>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-lg bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col max-h-[80vh] p-0">
        {/* Fixed Header */}
        <DialogHeader className="border-b border-gray-200 pb-4 px-6 pt-6 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Missing Timesheets
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading data...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          ) : defaulters.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-sm">No missing timesheets found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <TooltipProvider>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 border-b border-gray-200">
                      <TableHead className="w-12 font-semibold text-gray-700 py-2 px-3">#</TableHead>
                      <TableHead className="font-semibold text-gray-700 py-2 px-3">Employee Name</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uniqueUsers.map((user, index) => {
                      return (
                        <TableRow
                          key={user.userId}
                          className="border-b border-gray-100"
                        >
                          <TableCell className="font-medium text-gray-600 py-2 px-3">{index + 1}</TableCell>
                          <TableCell className="font-medium text-gray-900 py-2 px-3">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-3 cursor-pointer hover:text-red-600 transition-colors w-fit">
                                  <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-semibold text-red-600">
                                      {getInitials(user.userName)}
                                    </span>
                                  </div>
                                  <span className="text-sm">{user.userName}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent 
                                side="right" 
                                className="bg-red-50 text-red-900 border border-red-200 px-3 py-2 rounded-md shadow-lg max-w-xs"
                              >
                                <div className="text-sm">
                                  <div className="font-semibold mb-2">Pending Timesheets:</div>
                                  <ul className="space-y-1 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                    {user.timesheets.map((ts, idx) => (
                                      <li key={idx} className="flex items-start gap-2">
                                        <span className="text-red-500 mt-0.5 flex-shrink-0">•</span>
                                        <span className="leading-relaxed">{ts.duration}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TooltipProvider>
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="flex justify-end pt-4 pb-6 px-6 border-t border-gray-200 flex-shrink-0">
          <div className="text-sm text-gray-600">
            Total: <span className="font-semibold">{uniqueUsers.length}</span> employee{uniqueUsers.length !== 1 ? "s" : ""} with missing timesheet{uniqueUsers.length !== 1 ? "s" : ""}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}

